/**
 * TeamDashboard - Dashboard view showing team-specific metrics
 *
 * Displays:
 * - Quick insight cards with key metrics filtered by team
 * - Data table with detailed breakdown for selected team
 * - Team selector in page header
 * - Stacked bar charts showing commit distribution by classification and size
 */

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  ColorCodedBarChart,
  DataTable,
  DivergingBarChart,
  InsightDashboard,
  InsightDashboardStore,
  MetricsCard,
  PageHeader,
  type SelectOption,
  SimpleBarChart,
  StackedBarChart,
  type TabDefinition,
  TabsWithStore,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useState } from "react"
import { updateUrlParams } from "../hooks/useUrlParams"
import { getFromStorage, StorageKeys, saveToStorage } from "../lib/localStorage"
import { logger } from "../lib/logger"
import {
  calculateAverageDailyPerformance,
  calculateMetrics,
  calculatePerformanceScore,
} from "../lib/performanceScore"
import { routes } from "../routes/routes"
import type { ClassificationType } from "../stores/SettingsStore"
import {
  useDataStore,
  useI18nStore,
  useMetadataStore,
  useSettingsStore,
  useStore,
  useViewStore,
} from "../stores/StoreContext"
import type { ComplexityClass } from "../types/data"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

interface TeamDashboardProps {
  timeRange?: TimeRange
  /** When true, the component will sync the selected team to the URL. Default: false */
  syncToUrl?: boolean
  /** Currently selected tab ID (controlled mode) */
  selectedTab?: string
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void
  /** Initial team to select (from URL query param) */
  initialTeam?: string
}

// Helper to get the time range translation key
const getTimeRangeKey = (
  timeRange: TimeRange,
): "timeLast7Days" | "timeLast14Days" | "timeLast28Days" | "timeLast8Weeks" | "timeLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "timeLast7Days"
    case "14days":
      return "timeLast14Days"
    case "28days":
      return "timeLast28Days"
    case "8weeks":
      return "timeLast8Weeks"
    case "12weeks":
      return "timeLast12Weeks"
    default:
      return "timeLast7Days"
  }
}

// Helper to get the comparison translation key
const getComparisonKey = (
  timeRange: TimeRange,
):
  | "quickInsightVsLast7Days"
  | "quickInsightVsLast14Days"
  | "quickInsightVsLast28Days"
  | "quickInsightVsLast8Weeks"
  | "quickInsightVsLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "quickInsightVsLast7Days"
    case "14days":
      return "quickInsightVsLast14Days"
    case "28days":
      return "quickInsightVsLast28Days"
    case "8weeks":
      return "quickInsightVsLast8Weeks"
    case "12weeks":
      return "quickInsightVsLast12Weeks"
    default:
      return "quickInsightVsLast7Days"
  }
}

// Helper to get date range start based on time range
const getDateRangeStart = (timeRange: TimeRange): Date => {
  // Use yesterday as the reference point to exclude the current incomplete day
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // 14 days including yesterday
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // 28 days including yesterday
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (8 * 7 - 1) * 24 * 60 * 60 * 1000) // 8 weeks including yesterday
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (12 * 7 - 1) * 24 * 60 * 60 * 1000) // 12 weeks including yesterday
  }
  return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to get comparison period start based on time range
const getComparisonPeriodStart = (timeRange: TimeRange): Date => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // Previous 7 days
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // Previous 14 days
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 55 * 24 * 60 * 60 * 1000) // Previous 28 days
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (16 * 7 - 1) * 24 * 60 * 60 * 1000) // Previous 8 weeks
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (24 * 7 - 1) * 24 * 60 * 60 * 1000) // Previous 12 weeks
  }
  return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // Default to 14 days ago
}

// Helper to get number of weeks to show based on time range
const getWeeksToShow = (timeRange: TimeRange): number => {
  if (timeRange === "7days") {
    return 1 // Show at least 1 week for 7 days
  }
  if (timeRange === "14days") {
    return 2
  }
  if (timeRange === "28days") {
    return 4
  }
  if (timeRange === "8weeks") {
    return 8
  }
  if (timeRange === "12weeks") {
    return 12
  }
  return 6 // Default to 6 weeks
}

export const TeamDashboard = observer(
  ({
    timeRange = "7days",
    syncToUrl = false,
    selectedTab,
    onTabChange,
    initialTeam,
  }: TeamDashboardProps) => {
    const store = useStore()
    const dataStore = useDataStore()
    const metadataStore = useMetadataStore()
    const i18nStore = useI18nStore()
    const settingsStore = useSettingsStore()
    const viewStore = useViewStore()

    // Get the time range label for insight cards
    const timeRangeLabel = i18nStore.t(getTimeRangeKey(timeRange))
    const comparisonKey = getComparisonKey(timeRange)

    // Navigate to contributor page (Individual Dashboard)
    const navigateToContributor = useCallback(
      (handle: string) => {
        // Update URL params to show individual view with selected contributor
        updateUrlParams({ view: "individual", contributor: handle })
        // Trigger ViewStore update
        viewStore.setView("individual")
      },
      [viewStore],
    )

    // Calculate date ranges based on selected time range
    const dateRange = useMemo(() => getDateRangeStart(timeRange), [timeRange])
    const comparisonDateRange = useMemo(() => getComparisonPeriodStart(timeRange), [timeRange])

    // Get all unique teams
    const teams = metadataStore.uniqueTeams

    // Get team from URL route params if available
    const routeParams = store.router.params as { teamName?: string } | undefined
    const teamFromUrl = routeParams?.teamName ? decodeURIComponent(routeParams.teamName) : undefined

    // Get team from URL query params
    const queryParams = new URLSearchParams(window.location.search)
    const teamFromQuery = queryParams.get("team") || undefined

    // Initialize team selection from localStorage, URL, or default to first team
    const [selectedTeam, setSelectedTeam] = useState<string>(() => {
      // Priority: initialTeam > URL query param > URL route param > localStorage > first team
      if (initialTeam && teams.includes(initialTeam)) {
        return initialTeam
      }
      if (teamFromQuery && teams.includes(teamFromQuery)) {
        return teamFromQuery
      }
      if (teamFromUrl && teams.includes(teamFromUrl)) {
        return teamFromUrl
      }
      const saved = getFromStorage(StorageKeys.SELECTED_TEAM)
      if (saved && teams.includes(saved)) {
        return saved
      }
      return teams.length > 0 ? teams[0] : ""
    })

    // Initialize team selection based on URL or localStorage or default to first team
    useEffect(() => {
      if (teams.length > 0 && !selectedTeam) {
        if (initialTeam && teams.includes(initialTeam)) {
          setSelectedTeam(initialTeam)
        } else if (teamFromQuery && teams.includes(teamFromQuery)) {
          setSelectedTeam(teamFromQuery)
        } else if (teamFromUrl && teams.includes(teamFromUrl)) {
          setSelectedTeam(teamFromUrl)
        } else {
          const saved = getFromStorage(StorageKeys.SELECTED_TEAM)
          if (saved && teams.includes(saved)) {
            setSelectedTeam(saved)
          } else {
            setSelectedTeam(teams[0])
          }
        }
      }
    }, [teams, teamFromUrl, teamFromQuery, selectedTeam, initialTeam])

    // Sync URL when team changes (only if syncToUrl is enabled)
    useEffect(() => {
      if (
        syncToUrl &&
        selectedTeam &&
        selectedTeam !== teamFromUrl &&
        teams.includes(selectedTeam)
      ) {
        // Update URL to include the selected team
        store.router.goTo(routes.teamDetail, {
          teamName: encodeURIComponent(selectedTeam),
        } as Record<string, string>)
      }
    }, [syncToUrl, selectedTeam, teamFromUrl, store.router, teams])

    // Update selected team when URL changes externally
    useEffect(() => {
      if (teamFromUrl && teamFromUrl !== selectedTeam && teams.includes(teamFromUrl)) {
        setSelectedTeam(teamFromUrl)
      }
    }, [teamFromUrl, teams, selectedTeam])

    // Update selected team when query param changes
    useEffect(() => {
      if (teamFromQuery && teamFromQuery !== selectedTeam && teams.includes(teamFromQuery)) {
        setSelectedTeam(teamFromQuery)
      }
    }, [teamFromQuery, teams, selectedTeam])

    // Update selected team when initialTeam prop changes
    useEffect(() => {
      if (initialTeam && initialTeam !== selectedTeam && teams.includes(initialTeam)) {
        setSelectedTeam(initialTeam)
      }
    }, [initialTeam, teams, selectedTeam])

    // Handle team selection change
    const handleTeamChange = useCallback((teamId: string) => {
      setSelectedTeam(teamId)
      saveToStorage(StorageKeys.SELECTED_TEAM, teamId)
      // Update URL query params to reflect the new team selection
      updateUrlParams({ team: teamId })
    }, [])

    // Create select options for teams
    const teamOptions: SelectOption[] = useMemo(
      () =>
        teams.map((team) => ({
          id: team,
          label: team,
        })),
      [teams],
    )

    // Get author handles for selected team (from teams.json which uses handles as keys)
    const teamAuthorHandles = useMemo(() => {
      if (!selectedTeam) return new Set<string>()

      // teams.json maps author handle -> team name
      // We need to find all handles that belong to the selected team
      const teamMembers = metadataStore.allTeams
        .filter((t) => t.team === selectedTeam)
        .map((t) => t.authorId) // authorId here is actually the handle from teams.json key

      const handles = new Set(teamMembers)

      logger.debug("Team member lookup", {
        component: "TeamDashboard",
        team: selectedTeam,
        teamMembers,
        handles: Array.from(handles),
      })

      return handles
    }, [selectedTeam, metadataStore])

    // Create the dashboard store with computed data filtered by team
    const dashboardStore = useMemo(() => {
      // Filter commits and PRs by team members
      // Note: commits use authorHandle (from DataStore.getAuthorHandle), not authorId
      const commits = dataStore.allCommits.filter((c) => teamAuthorHandles.has(c.authorHandle))
      const prs = dataStore.allPullRequests.filter((pr) => teamAuthorHandles.has(pr.creatorHandle))

      logger.debug("Filtered data for team", {
        component: "TeamDashboard",
        team: selectedTeam,
        totalCommits: dataStore.allCommits.length,
        filteredCommits: commits.length,
        totalPRs: dataStore.allPullRequests.length,
        filteredPRs: prs.length,
        teamAuthorHandles: Array.from(teamAuthorHandles),
        sampleCommitHandles: dataStore.allCommits.slice(0, 5).map((c) => c.authorHandle),
        samplePRHandles: dataStore.allPullRequests.slice(0, 5).map((pr) => pr.creatorHandle),
      })

      // Use computed date ranges (up to yesterday)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(23, 59, 59, 999)
      const currentPeriodStart = dateRange
      const previousPeriodStart = comparisonDateRange

      // Helper function to generate daily chart data
      // Excludes weekends (Saturday/Sunday) with no activity
      const generateDailyChartData = (
        items: Array<{ timestampDate?: Date; createdAtDate?: Date }>,
        startDate: Date,
        endDate: Date,
      ) => {
        const dailyCounts = new Map<string, number>()
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)

        // Initialize all days with 0
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split("T")[0]
          dailyCounts.set(dateKey, 0)
        }

        // Count items per day
        items.forEach((item) => {
          const date = item.timestampDate || item.createdAtDate
          if (date && date >= start && date <= end) {
            const dateKey = date.toISOString().split("T")[0]
            dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
          }
        })

        // Filter out weekends with no activity
        return Array.from(dailyCounts.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .filter(([dateKey, value]) => {
            const date = new Date(dateKey)
            const dayOfWeek = date.getDay()
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
            // Include if it's not a weekend, or if it's a weekend with activity
            return !isWeekend || value > 0
          })
          .map(([, value]) => ({ value }))
      }

      // Current period metrics (based on selected time range)
      const currentWeekPRs = prs.filter(
        (pr) => pr.createdAtDate >= currentPeriodStart && pr.state === "merged",
      )
      const currentWeekFeatures = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "feature",
      )
      const currentWeekBugfixes = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "bugfix",
      )
      const currentWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      // Previous period metrics (for comparison)
      const previousWeekPRs = prs.filter(
        (pr) =>
          pr.createdAtDate >= previousPeriodStart &&
          pr.createdAtDate < currentPeriodStart &&
          pr.state === "merged",
      )
      const previousWeekFeatures = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "feature",
      )
      const previousWeekBugfixes = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "bugfix",
      )
      const previousWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      logger.debug("Calculated team metrics", {
        component: "TeamDashboard",
        team: selectedTeam,
        teamMembers: teamAuthorHandles.size,
        currentWeekPRs: currentWeekPRs.length,
        currentWeekFeatures: currentWeekFeatures.length,
        currentWeekBugfixes: currentWeekBugfixes.length,
        currentWeekMaintenance: currentWeekMaintenance.length,
      })

      // Calculate team-wide performance score
      const allCurrentCommits = commits.filter((c) => c.timestampDate >= currentPeriodStart)
      const allCurrentIssues = dataStore.allIssues.filter((issue) => {
        const closedByInTeam = issue.closedByHandle && teamAuthorHandles.has(issue.closedByHandle)
        const createdByInTeam = teamAuthorHandles.has(issue.creatorHandle)
        return (
          issue.closedAtDate &&
          issue.closedAtDate >= currentPeriodStart &&
          (closedByInTeam || createdByInTeam)
        )
      })

      // Calculate average daily performance score (excluding weekends with no activity)
      const avgDailyScore = calculateAverageDailyPerformance(
        allCurrentCommits,
        currentWeekPRs,
        allCurrentIssues,
        currentPeriodStart,
        yesterday,
      )

      // Create a performance score object with the calculated average
      const performanceScore = {
        overall: avgDailyScore,
        breakdown: {
          quality: 0,
          throughput: 0,
          impact: 0,
          problemSolving: 0,
          versatility: 0,
        },
        metrics: {
          commitScore: 0,
          commitCount: 0,
          linesAdded: 0,
          linesRemoved: 0,
          filesChanged: 0,
          prCount: 0,
          prAdditions: 0,
          prDeletions: 0,
          issuesResolved: 0,
          featureCommits: 0,
          bugfixCommits: 0,
          refactoringCommits: 0,
          otherCommits: 0,
        },
      }

      // Generate daily performance values for the chart
      const dailyPerformanceData = generateDailyChartData(
        allCurrentCommits,
        currentPeriodStart,
        yesterday,
      ).map((_, index) => {
        const dayStart = new Date(currentPeriodStart)
        dayStart.setDate(dayStart.getDate() + index)
        dayStart.setHours(0, 0, 0, 0)
        const dayEnd = new Date(dayStart)
        dayEnd.setHours(23, 59, 59, 999)

        const dayCommits = allCurrentCommits.filter(
          (c) => c.timestampDate >= dayStart && c.timestampDate <= dayEnd,
        )
        const dayPRs = currentWeekPRs.filter(
          (pr) => pr.createdAtDate && pr.createdAtDate >= dayStart && pr.createdAtDate <= dayEnd,
        )
        const dayIssues = allCurrentIssues.filter(
          (issue) =>
            issue.closedAtDate && issue.closedAtDate >= dayStart && issue.closedAtDate <= dayEnd,
        )

        const dayMetrics = calculateMetrics(dayCommits, dayPRs, dayIssues)
        const dayScore = calculatePerformanceScore(dayMetrics, 1)

        return {
          value: dayScore.overall,
          date: dayStart,
          metrics: dayMetrics,
          breakdown: dayScore.breakdown,
        }
      })

      const dailyPerformanceValues = dailyPerformanceData.map((d) => ({ value: d.value }))

      // Calculate trend for performance score (compare first half vs second half)
      const halfwayPoint = Math.floor(dailyPerformanceData.length / 2)
      const firstHalfAvg =
        dailyPerformanceData.slice(0, halfwayPoint).reduce((sum, d) => sum + d.value, 0) /
          halfwayPoint || 0
      const secondHalfAvg =
        dailyPerformanceData.slice(halfwayPoint).reduce((sum, d) => sum + d.value, 0) /
          (dailyPerformanceData.length - halfwayPoint) || 0
      const performanceTrend = secondHalfAvg - firstHalfAvg

      // Create insights with their associated table data
      const insights = [
        {
          id: "pr-throughput",
          insight: {
            id: "pr-throughput",
            subtitle: i18nStore.t("insightMergedPRs"),
            date: timeRangeLabel,
            title: i18nStore.t("insightPRThroughput"),
            value: currentWeekPRs.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekPRs.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekPRs, currentPeriodStart, yesterday),
          },
          tableData: currentWeekPRs.map((pr) => ({
            title: pr.title,
            url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
            author: pr.creatorHandle,
            date: pr.createdAtDate.toLocaleDateString(),
            additions: pr.additions?.toString() || "0",
            deletions: pr.deletions?.toString() || "0",
            files: pr.changedFiles?.toString() || "0",
          })),
          tableColumns: [
            {
              accessorKey: "title",
              header: i18nStore.t("tableColumnTitle"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("title")}
                </a>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = `https://github.com/${encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))}.png?size=64`
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            {
              accessorKey: "date",
              header: i18nStore.t("tableColumnCreated"),
            },
            {
              accessorKey: "additions",
              header: i18nStore.t("tableColumnAdditions"),
            },
            {
              accessorKey: "deletions",
              header: i18nStore.t("tableColumnDeletions"),
            },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "features",
          insight: {
            id: "features",
            subtitle: i18nStore.t("insightFeatureCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightFeatures"),
            value: currentWeekFeatures.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekFeatures.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekFeatures, currentPeriodStart, yesterday),
          },
          tableData: currentWeekFeatures.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = `https://github.com/${encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))}.png?size=64`
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "bugfixes",
          insight: {
            id: "bugfixes",
            subtitle: i18nStore.t("insightBugfixCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightBugfixes"),
            value: currentWeekBugfixes.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekBugfixes.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekBugfixes, currentPeriodStart, yesterday),
          },
          tableData: currentWeekBugfixes.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = `https://github.com/${encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))}.png?size=64`
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "performance",
          insight: {
            id: "performance",
            subtitle: i18nStore.t("performanceScoreTitle"),
            date: timeRangeLabel,
            title: i18nStore.t("tabPerformance"),
            value: performanceScore.overall.toFixed(1),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: (performanceTrend >= 0 ? "+" : "") + performanceTrend.toFixed(1),
            }),
            chartData: dailyPerformanceValues,
          },
          tableData: dailyPerformanceData
            .map((dayData) => {
              const m = dayData.metrics
              const b = dayData.breakdown

              // Build concise reasoning based on actual activity and its impact
              const reasons = []
              if (m.commitCount > 0) {
                reasons.push(`${m.commitCount} commits`)
              }
              if (m.prCount > 0) {
                reasons.push(`${m.prCount} PRs`)
              }
              if (m.linesAdded + m.linesRemoved > 0) {
                reasons.push(`${m.linesAdded + m.linesRemoved} lines`)
              }
              if (m.issuesResolved > 0) {
                reasons.push(`${m.issuesResolved} issues`)
              }

              // Add component scores to show impact on overall score
              const impactScores = []
              if (b.quality > 0) impactScores.push(`Q:${b.quality.toFixed(0)}`)
              if (b.throughput > 0) impactScores.push(`T:${b.throughput.toFixed(0)}`)
              if (b.impact > 0) impactScores.push(`I:${b.impact.toFixed(0)}`)
              if (b.problemSolving > 0) impactScores.push(`PS:${b.problemSolving.toFixed(0)}`)
              if (b.versatility > 0) impactScores.push(`V:${b.versatility.toFixed(0)}`)

              const activityText = reasons.length > 0 ? reasons.join(", ") : "No activity"
              const impactText = impactScores.length > 0 ? ` → ${impactScores.join(", ")}` : ""

              // Add formula explanation showing how the score is calculated
              // Format: Q×30% + T×25% + I×20% + PS×15% + V×10%
              const formulaParts = []
              if (b.quality > 0) formulaParts.push(`${b.quality.toFixed(0)}×30%`)
              if (b.throughput > 0) formulaParts.push(`${b.throughput.toFixed(0)}×25%`)
              if (b.impact > 0) formulaParts.push(`${b.impact.toFixed(0)}×20%`)
              if (b.problemSolving > 0) formulaParts.push(`${b.problemSolving.toFixed(0)}×15%`)
              if (b.versatility > 0) formulaParts.push(`${b.versatility.toFixed(0)}×10%`)

              const formulaText = formulaParts.length > 0 ? ` = ${formulaParts.join(" + ")}` : ""

              // Get weekday name
              const weekdayNames = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ]
              const weekday = weekdayNames[dayData.date.getDay()]

              // Check if it's a weekend with no activity
              const isWeekend = dayData.date.getDay() === 0 || dayData.date.getDay() === 6
              const hasActivity = m.commitCount > 0 || m.prCount > 0 || m.issuesResolved > 0
              const isExcluded = isWeekend && !hasActivity

              return {
                date: dayData.date.toLocaleDateString(),
                weekday: weekday,
                value: dayData.value.toFixed(1),
                reasoning: activityText + impactText + formulaText,
                isExcluded: isExcluded,
              }
            })
            .reverse(), // Most recent first
          tableColumns: [
            {
              accessorKey: "date",
              header: i18nStore.t("tableColumnDate"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; date: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.date}
                </span>
              ),
            },
            {
              accessorKey: "weekday",
              header: i18nStore.t("tableColumnWeekday"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; weekday: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.weekday}
                </span>
              ),
            },
            {
              accessorKey: "value",
              header: i18nStore.t("tableColumnValue"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; value: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.value}
                </span>
              ),
            },
            {
              accessorKey: "reasoning",
              header: i18nStore.t("tableColumnReasoning"),
              cell: ({
                row,
              }: {
                row: { original: { isExcluded: boolean; reasoning: string } }
              }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.reasoning}
                </span>
              ),
            },
          ],
        },
        // Maintenance insight kept for potential future use
        {
          id: "maintenance",
          insight: {
            id: "maintenance",
            subtitle: i18nStore.t("insightMaintenanceCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightMaintenance"),
            value: currentWeekMaintenance.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekMaintenance.length.toString(),
            }),
            chartData: generateDailyChartData(
              currentWeekMaintenance,
              currentPeriodStart,
              yesterday,
            ),
          },
          tableData: currentWeekMaintenance.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            type: commit.commitType || "other",
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string }
                  getValue: (key: string) => string
                }
              }) => (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = `https://github.com/${encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))}.png?size=64`
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "type", header: i18nStore.t("tableColumnType") },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
      ]

      return InsightDashboardStore.create({
        insights: insights.slice(0, 4), // Only show pr-throughput, features, bugfixes, and performance
        selectedInsightId: "pr-throughput",
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale needed for translation reactivity
    }, [
      dataStore.allCommits,
      dataStore.allPullRequests,
      dataStore.allIssues,
      teamAuthorHandles,
      selectedTeam,
      i18nStore.t,
      i18nStore.tf,
      dateRange,
      comparisonDateRange,
      timeRangeLabel,
      comparisonKey,
      navigateToContributor,
    ])

    // Compute bar chart data for team commits
    const barChartData = useMemo(() => {
      const selectedClassifications = settingsStore.selectedClassificationsArray

      // Map commit types to avoid reserved words (for display purposes only)
      const mapCommitType = (type: string | undefined): string => {
        if (type === "style") return "styling"
        return type || "unclassified"
      }

      // Filter commits by team members and selected classifications
      // Note: Use original commitType for filtering, mapCommitType is only for display
      const commits = dataStore.allCommits.filter((c) => {
        const classType = c.commitType || "unclassified"
        return (
          teamAuthorHandles.has(c.authorHandle) &&
          selectedClassifications.includes(classType as ClassificationType)
        )
      })

      // Define the order of complexity classes and commit types
      const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
      const commitTypeOrder = [
        "feature",
        "bugfix",
        "refactoring",
        "ci",
        "tests",
        "docs",
        "chore",
        "perf",
        "revert",
        "merge",
        "archive",
        "styling", // Renamed from "style" to avoid React reserved prop conflict
        "other",
      ]

      // Chart 1: Classification (x-axis) stacked by Size
      const byClassification = new Map<string, Record<ComplexityClass, number>>()

      for (const commitType of commitTypeOrder) {
        byClassification.set(commitType, {
          XS: 0,
          S: 0,
          M: 0,
          L: 0,
          XL: 0,
        })
      }

      byClassification.set("unclassified", {
        XS: 0,
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
      })

      for (const commit of commits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = byClassification.get(type)
        if (counts && size in counts) {
          counts[size] = (counts[size] || 0) + 1
        }
      }

      const classificationData = Array.from(byClassification.entries())
        .filter(([type, counts]) => {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = type === "styling" ? "style" : type
          return (
            selectedClassifications.includes(originalType as ClassificationType) &&
            Object.values(counts).some((v) => v > 0)
          )
        })
        .map(([type, counts]) => ({
          classification: type,
          ...counts,
        }))

      // Chart 2: Size (x-axis) stacked by Classification
      const bySize = new Map<ComplexityClass, Record<string, number>>()

      for (const size of complexityOrder) {
        const typeCounts: Record<string, number> = {}
        for (const type of [...commitTypeOrder, "unclassified"]) {
          typeCounts[type] = 0
        }
        bySize.set(size, typeCounts)
      }

      for (const commit of commits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = bySize.get(size)
        if (counts) {
          counts[type] = (counts[type] || 0) + 1
        }
      }

      const activeTypes = new Set<string>()
      for (const [, counts] of bySize.entries()) {
        for (const [type, count] of Object.entries(counts)) {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = type === "styling" ? "style" : type
          if (count > 0 && selectedClassifications.includes(originalType as ClassificationType)) {
            activeTypes.add(type)
          }
        }
      }

      const sizeData = complexityOrder.map((size) => {
        const counts = bySize.get(size)
        if (!counts) return { size }
        const entry: Record<string, string | number> = { size }
        for (const type of activeTypes) {
          entry[type] = counts[type] || 0
        }
        return entry
      })

      return {
        classificationData,
        sizeData,
        complexityOrder,
        activeTypes: Array.from(activeTypes),
      }
    }, [dataStore.allCommits, teamAuthorHandles, settingsStore.selectedClassificationsArray])

    // Calculate PR metrics for the new PR tab
    const prMetrics = useMemo(() => {
      const prs = dataStore.allPullRequests.filter((pr) => teamAuthorHandles.has(pr.creatorHandle))
      const now = new Date()
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

      // Current month PRs
      const currentMonthPRs = prs.filter(
        (pr) => pr.mergedAtDate && pr.mergedAtDate >= oneMonthAgo && pr.state === "merged",
      )

      // Previous month PRs (for comparison)
      const previousMonthPRs = prs.filter(
        (pr) =>
          pr.mergedAtDate &&
          pr.mergedAtDate >= twoMonthsAgo &&
          pr.mergedAtDate < oneMonthAgo &&
          pr.state === "merged",
      )

      // Calculate percentage change
      const currentCount = currentMonthPRs.length
      const previousCount = previousMonthPRs.length
      const percentageChange =
        previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : 0

      // Generate daily PR count for chart (last 30 days)
      const dailyPRs = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyPRs.set(dateKey, 0)
      }

      currentMonthPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          dailyPRs.set(dateKey, (dailyPRs.get(dateKey) || 0) + 1)
        }
      })

      const prChartData = Array.from(dailyPRs.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Calculate code changes (additions + deletions) for current month
      const dailyCodeChanges = new Map<string, { additions: number; deletions: number }>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyCodeChanges.set(dateKey, { additions: 0, deletions: 0 })
      }

      currentMonthPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          const current = dailyCodeChanges.get(dateKey) || {
            additions: 0,
            deletions: 0,
          }
          dailyCodeChanges.set(dateKey, {
            additions: current.additions + pr.additions,
            deletions: current.deletions + pr.deletions,
          })
        }
      })

      const codeChangesChartData = Array.from(dailyCodeChanges.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { additions, deletions }]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: additions + deletions,
        }))

      return {
        currentCount,
        previousCount,
        percentageChange,
        prChartData,
        codeChangesChartData,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
      }
    }, [dataStore.allPullRequests, teamAuthorHandles])

    // Calculate weekly PR data for the PRs tab
    const weeklyPRData = useMemo(() => {
      const prs = dataStore.allPullRequests.filter(
        (pr) => pr.state === "merged" && teamAuthorHandles.has(pr.creatorHandle),
      )
      const now = new Date()
      const weeksToShow = getWeeksToShow(timeRange)
      const weeksAgo = new Date(now.getTime() - weeksToShow * 7 * 24 * 60 * 60 * 1000)

      // Filter PRs from the specified time range
      const recentPRs = prs.filter((pr) => pr.mergedAtDate && pr.mergedAtDate >= weeksAgo)

      if (recentPRs.length === 0) {
        return {
          prsByWeek: [],
          prsByWeekByContributor: [],
          codeChangesByWeek: [],
          contributors: [],
        }
      }

      // Get the earliest PR date to determine actual weeks available
      const earliestDate = new Date(
        Math.min(
          ...recentPRs
            .filter((pr) => pr.mergedAtDate)
            .map((pr) => (pr.mergedAtDate as Date).getTime()),
        ),
      )
      const weeksAvailable = Math.min(
        weeksToShow,
        Math.ceil((now.getTime() - earliestDate.getTime()) / (7 * 24 * 60 * 60 * 1000)),
      )

      // Helper function to get week label
      const getWeekLabel = (weekIndex: number, totalWeeks: number): string => {
        if (weekIndex === totalWeeks - 1) return "Current Week"
        if (weekIndex === totalWeeks - 2) return "Last Week"

        // Get the first day of the week (Monday)
        const weekStart = new Date(
          now.getTime() - (totalWeeks - weekIndex) * 7 * 24 * 60 * 60 * 1000,
        )
        const monday = new Date(weekStart)
        monday.setDate(weekStart.getDate() - weekStart.getDay() + 1)

        const month = String(monday.getMonth() + 1).padStart(2, "0")
        const day = String(monday.getDate()).padStart(2, "0")
        return `${month}/${day}`
      }

      // Get all unique contributors from team
      const allContributors = Array.from(teamAuthorHandles).sort()

      // Group PRs by week
      const weeklyData: {
        label: string
        count: number
        additions: number
        deletions: number
        byContributor: Map<string, number>
      }[] = []

      for (let i = 0; i < weeksAvailable; i++) {
        const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000)
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
        const weekLabel = getWeekLabel(weeksAvailable - i - 1, weeksAvailable)

        const weekPRs = recentPRs.filter(
          (pr) => pr.mergedAtDate && pr.mergedAtDate >= weekStart && pr.mergedAtDate < weekEnd,
        )

        const additions = weekPRs.reduce((sum, pr) => sum + pr.additions, 0)
        const deletions = weekPRs.reduce((sum, pr) => sum + pr.deletions, 0)

        // Count PRs by contributor
        const byContributor = new Map<string, number>()

        // Initialize all contributors with 0
        for (const contributor of allContributors) {
          byContributor.set(contributor, 0)
        }

        // Count PRs per contributor
        weekPRs.forEach((pr) => {
          if (teamAuthorHandles.has(pr.creatorHandle)) {
            byContributor.set(pr.creatorHandle, (byContributor.get(pr.creatorHandle) || 0) + 1)
          }
        })

        weeklyData.push({
          label: weekLabel,
          count: weekPRs.length,
          additions,
          deletions,
          byContributor,
        })
      }

      // Reverse to show oldest first
      weeklyData.reverse()

      // Format for regular bar chart (PR count)
      const prsByWeek = weeklyData.map((week) => ({
        label: week.label,
        value: week.count,
      }))

      // Format for stacked bar chart (PR count by contributor)
      const prsByWeekByContributor = weeklyData.map((week) => {
        const entry: Record<string, string | number> = { week: week.label }
        for (const contributor of allContributors) {
          entry[contributor] = week.byContributor.get(contributor) || 0
        }
        return entry
      })

      // Format for diverging bar chart (additions/deletions)
      const codeChangesByWeek = weeklyData.map((week) => ({
        label: week.label,
        positive: week.additions,
        negative: -week.deletions, // Negative for downward bars
      }))

      return {
        prsByWeek,
        prsByWeekByContributor,
        codeChangesByWeek,
        contributors: allContributors,
      }
    }, [dataStore.allPullRequests, teamAuthorHandles, timeRange])

    // Calculate Issue metrics for the new Issues tab
    const issueMetrics = useMemo(() => {
      const issues = dataStore.allIssues.filter((issue) =>
        teamAuthorHandles.has(issue.creatorHandle),
      )
      const now = new Date()
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

      // Current month issues created
      const currentMonthCreated = issues.filter((issue) => issue.createdAtDate >= oneMonthAgo)

      // Previous month issues created (for comparison)
      const previousMonthCreated = issues.filter(
        (issue) => issue.createdAtDate >= twoMonthsAgo && issue.createdAtDate < oneMonthAgo,
      )

      // Current month issues closed
      const currentMonthClosed = issues.filter(
        (issue) =>
          issue.closedAtDate && issue.closedAtDate >= oneMonthAgo && issue.state === "closed",
      )

      // Previous month issues closed (for comparison)
      const previousMonthClosed = issues.filter(
        (issue) =>
          issue.closedAtDate &&
          issue.closedAtDate >= twoMonthsAgo &&
          issue.closedAtDate < oneMonthAgo &&
          issue.state === "closed",
      )

      // Calculate percentage changes
      const createdCount = currentMonthCreated.length
      const previousCreatedCount = previousMonthCreated.length
      const createdPercentageChange =
        previousCreatedCount > 0
          ? ((createdCount - previousCreatedCount) / previousCreatedCount) * 100
          : 0

      const closedCount = currentMonthClosed.length
      const previousClosedCount = previousMonthClosed.length
      const closedPercentageChange =
        previousClosedCount > 0
          ? ((closedCount - previousClosedCount) / previousClosedCount) * 100
          : 0

      // Generate daily issue created count for chart (last 30 days)
      const dailyCreated = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyCreated.set(dateKey, 0)
      }

      currentMonthCreated.forEach((issue) => {
        const dateKey = issue.createdAtDate.toISOString().split("T")[0]
        dailyCreated.set(dateKey, (dailyCreated.get(dateKey) || 0) + 1)
      })

      const createdChartData = Array.from(dailyCreated.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Generate daily issue closed count for chart (last 30 days)
      const dailyClosed = new Map<string, number>()
      for (let i = 0; i < 30; i++) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
        const dateKey = date.toISOString().split("T")[0]
        dailyClosed.set(dateKey, 0)
      }

      currentMonthClosed.forEach((issue) => {
        if (issue.closedAtDate) {
          const dateKey = issue.closedAtDate.toISOString().split("T")[0]
          dailyClosed.set(dateKey, (dailyClosed.get(dateKey) || 0) + 1)
        }
      })

      const closedChartData = Array.from(dailyClosed.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      return {
        createdCount,
        previousCreatedCount,
        createdPercentageChange,
        createdChartData,
        createdTrend: (createdPercentageChange > 0
          ? "up"
          : createdPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
        closedCount,
        previousClosedCount,
        closedPercentageChange,
        closedChartData,
        closedTrend: (closedPercentageChange > 0
          ? "up"
          : closedPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
      }
    }, [dataStore.allIssues, teamAuthorHandles])

    // Calculate releases metrics for team
    const releasesMetrics = useMemo(() => {
      const now = new Date()

      // Helper to get Monday of the current week
      const getMondayOfWeek = (date: Date): Date => {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
        return new Date(d.setDate(diff))
      }

      // Helper to get Sunday of a given Monday
      const getSundayOfWeek = (monday: Date): Date => {
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return sunday
      }

      // Get Monday of current week
      const currentWeekMonday = getMondayOfWeek(now)
      currentWeekMonday.setHours(0, 0, 0, 0)

      // Calculate start of 12 weeks ago (Monday of that week)
      const twelveWeeksAgoMonday = new Date(currentWeekMonday)
      twelveWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 12 * 7)

      // Calculate start of 6 weeks ago (Monday of that week)
      const sixWeeksAgoMonday = new Date(currentWeekMonday)
      sixWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 6 * 7)

      // Get repositories assigned to this team
      const teamRepositories = metadataStore.getRepositoriesByGroup(selectedTeam)
      const teamRepoSet = new Set(teamRepositories)

      logger.debug("Team repositories lookup", {
        component: "TeamDashboard",
        team: selectedTeam,
        repositories: teamRepositories,
      })

      // Filter releases by team's repositories (excluding drafts and prereleases)
      const recentReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= twelveWeeksAgoMonday &&
          !release.draft &&
          !release.prerelease &&
          teamRepoSet.has(release.repoName),
      )

      // Count total releases in last 12 weeks
      const totalReleases = recentReleases.length

      // Create weekly buckets for trend (last 12 weeks)
      const weeklyTrend: Array<{ label: string; value: number }> = []
      for (let i = 0; i < 12; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (11 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const count = recentReleases.filter(
          (release) => release.createdAtDate >= weekMonday && release.createdAtDate <= weekSunday,
        ).length

        weeklyTrend.push({
          label: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: count,
        })
      }

      // Calculate percentage change (comparing first 6 weeks vs last 6 weeks)
      const firstHalf = weeklyTrend.slice(0, 6).reduce((sum, week) => sum + week.value, 0)
      const secondHalf = weeklyTrend.slice(6, 12).reduce((sum, week) => sum + week.value, 0)
      const percentageChange = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0

      // Create stacked bar chart data for last 6 weeks by repository
      const last6WeeksReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= sixWeeksAgoMonday &&
          !release.draft &&
          !release.prerelease &&
          teamRepoSet.has(release.repoName),
      )

      // Get all unique repositories
      const repositories = Array.from(new Set(last6WeeksReleases.map((r) => r.repoName))).sort()

      // Create weekly buckets for stacked chart
      const stackedData: Array<Record<string, string | number>> = []
      for (let i = 0; i < 6; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (5 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const weekData: Record<string, string | number> = {
          week: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        }

        // Count releases per repository for this week
        repositories.forEach((repo) => {
          const count = last6WeeksReleases.filter(
            (release) =>
              release.repoName === repo &&
              release.createdAtDate >= weekMonday &&
              release.createdAtDate <= weekSunday,
          ).length
          weekData[repo] = count
        })

        stackedData.push(weekData)
      }

      return {
        totalReleases,
        weeklyTrend,
        percentageChange,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
        stackedData,
        repositories,
      }
    }, [dataStore.allReleases, selectedTeam, metadataStore])

    // Calculate top contributors (team members + external) based on selected metric
    const topContributors = useMemo(() => {
      const currentPeriodStart = dateRange
      const selectedInsight = dashboardStore.selectedInsight

      if (!selectedInsight || !selectedTeam) {
        return []
      }

      // Get repositories assigned to this team
      const teamRepositories = metadataStore.getRepositoriesByGroup(selectedTeam)
      const teamRepoSet = new Set(teamRepositories)

      // Determine metric type from selected insight
      const metricType = selectedInsight.id // pr-throughput, features, bugfixes, maintenance

      // Aggregate by contributor (team members + external)
      const teamMemberMetrics = new Map<string, number>()
      const externalMetrics = new Map<string, number>()

      if (metricType === "pr-throughput") {
        // Filter PRs by time range and team repositories
        const filteredPRs = dataStore.allPullRequests.filter(
          (pr) =>
            pr.createdAtDate >= currentPeriodStart &&
            pr.state === "merged" &&
            teamRepoSet.has(pr.repoName),
        )

        // Count by contributor, separating team members from external
        filteredPRs.forEach((pr) => {
          const isTeamMember = teamAuthorHandles.has(pr.creatorHandle)
          if (isTeamMember) {
            teamMemberMetrics.set(
              pr.creatorHandle,
              (teamMemberMetrics.get(pr.creatorHandle) || 0) + 1,
            )
          } else {
            externalMetrics.set(pr.creatorHandle, (externalMetrics.get(pr.creatorHandle) || 0) + 1)
          }
        })
      } else {
        // For commit-based metrics (features, bugfixes, maintenance)
        let filteredCommits = dataStore.allCommits.filter(
          (c) => c.timestampDate >= currentPeriodStart && teamRepoSet.has(c.repoName),
        )

        // Filter by commit type
        if (metricType === "features") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "feature")
        } else if (metricType === "bugfixes") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "bugfix")
        } else if (metricType === "maintenance") {
          filteredCommits = filteredCommits.filter(
            (c) => c.commitType && c.commitType !== "feature" && c.commitType !== "bugfix",
          )
        }

        // Count by contributor, separating team members from external
        filteredCommits.forEach((commit) => {
          const isTeamMember = teamAuthorHandles.has(commit.authorHandle)
          if (isTeamMember) {
            teamMemberMetrics.set(
              commit.authorHandle,
              (teamMemberMetrics.get(commit.authorHandle) || 0) + 1,
            )
          } else {
            externalMetrics.set(
              commit.authorHandle,
              (externalMetrics.get(commit.authorHandle) || 0) + 1,
            )
          }
        })
      }

      // Combine contributors from both maps
      const allContributors: Array<{
        label: string
        value: number
        color: "primary" | "secondary"
      }> = []

      // Add team members with primary color
      teamMemberMetrics.forEach((count, contributor) => {
        allContributors.push({
          label: contributor,
          value: count,
          color: "primary",
        })
      })

      // Add external contributors with secondary color
      externalMetrics.forEach((count, contributor) => {
        allContributors.push({
          label: contributor,
          value: count,
          color: "secondary",
        })
      })

      // Sort by value descending
      return allContributors.sort((a, b) => b.value - a.value)
    }, [
      dashboardStore.selectedInsight,
      dataStore.allCommits,
      dataStore.allPullRequests,
      teamAuthorHandles,
      dateRange,
      selectedTeam,
      metadataStore,
    ])

    // Calculate top repositories for the team based on selected metric
    const topTeamRepositories = useMemo(() => {
      const currentPeriodStart = dateRange
      const selectedInsight = dashboardStore.selectedInsight

      if (!selectedInsight || !selectedTeam) {
        return []
      }

      // Determine metric type from selected insight
      const metricType = selectedInsight.id // pr-throughput, features, bugfixes, maintenance

      // Aggregate by repository (team members only)
      const repositoryMetrics = new Map<string, number>()

      if (metricType === "pr-throughput") {
        // Filter PRs by time range and team members
        const filteredPRs = dataStore.allPullRequests.filter(
          (pr) =>
            pr.createdAtDate >= currentPeriodStart &&
            pr.state === "merged" &&
            teamAuthorHandles.has(pr.creatorHandle),
        )

        // Count by repository
        filteredPRs.forEach((pr) => {
          repositoryMetrics.set(pr.repoName, (repositoryMetrics.get(pr.repoName) || 0) + 1)
        })
      } else {
        // For commit-based metrics (features, bugfixes, maintenance)
        let filteredCommits = dataStore.allCommits.filter(
          (c) => c.timestampDate >= currentPeriodStart && teamAuthorHandles.has(c.authorHandle),
        )

        // Filter by commit type
        if (metricType === "features") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "feature")
        } else if (metricType === "bugfixes") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "bugfix")
        } else if (metricType === "maintenance") {
          filteredCommits = filteredCommits.filter(
            (c) => c.commitType && c.commitType !== "feature" && c.commitType !== "bugfix",
          )
        }

        // Count by repository
        filteredCommits.forEach((commit) => {
          repositoryMetrics.set(commit.repoName, (repositoryMetrics.get(commit.repoName) || 0) + 1)
        })
      }

      // Sort by value descending and format for bar chart
      return Array.from(repositoryMetrics.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([repository, value]) => ({
          label: repository,
          value,
        }))
    }, [
      dashboardStore.selectedInsight,
      dataStore.allCommits,
      dataStore.allPullRequests,
      teamAuthorHandles,
      dateRange,
      selectedTeam,
    ])

    // Create tabs
    const tabs: TabDefinition[] = [
      {
        id: "insights",
        label: i18nStore.t("tabPerformance"),
        content: (
          <div className="space-y-6">
            {/* Always show quick insight cards */}
            <InsightDashboard
              store={dashboardStore}
              tableTitle={i18nStore.t("chartDetails")}
              dataTableTranslations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              hideTable={true}
            />

            {/* Show performance explanation and details table when performance is selected */}
            {dashboardStore.selectedInsight?.id === "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <div className="space-y-4">
                  {/* Performance Explanation */}
                  <div className="rounded-lg border bg-card p-6">
                    <h3 className="text-lg font-semibold mb-3">
                      {i18nStore.t("performanceMethodologyTitle")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {i18nStore.t("performanceMethodologyDescription")}
                    </p>
                    <ul className="space-y-2 text-sm mb-4">
                      <li>
                        <strong>{i18nStore.t("performanceQualityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceQualityDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceThroughputWeight")}</strong>:{" "}
                        {i18nStore.t("performanceThroughputDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceImpactWeight")}</strong>:{" "}
                        {i18nStore.t("performanceImpactDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceProblemSolvingWeight")}</strong>:{" "}
                        {i18nStore.t("performanceProblemSolvingDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceVersatilityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceVersatilityDesc")}
                      </li>
                    </ul>
                    <p className="text-sm text-muted-foreground italic border-t pt-3">
                      {i18nStore.t("performanceScoreNote")}
                    </p>
                  </div>

                  {/* Performance Daily Values Table */}
                  <DataTable
                    title={i18nStore.t("performanceDailyValuesTitle")}
                    // biome-ignore lint/suspicious/noExplicitAny: MST frozen type requires any cast
                    columns={dashboardStore.selectedTableColumns as any}
                    data={dashboardStore.selectedTableData}
                    enableSearch={true}
                    searchPlaceholder={i18nStore.t("dataTableSearch")}
                    enableCsvExport={true}
                    csvFilename={`performance-daily-${selectedTeam}.csv`}
                    initialSorting={[{ id: "date", desc: true }]}
                    translations={{
                      filtersButton: i18nStore.t("dataTableFilters"),
                      filterBy: i18nStore.t("dataTableFilterBy"),
                      noResults: i18nStore.t("dataTableNoResults"),
                      rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                      page: i18nStore.t("dataTablePage"),
                      of: i18nStore.t("dataTableOf"),
                      totalRows: i18nStore.t("dataTableTotalRows"),
                    }}
                  />
                </div>
              )}

            {/* Two-column grid with top contributors and top repositories (only when NOT performance) */}
            {dashboardStore.selectedInsight?.id !== "performance" && (
              <div className="grid gap-6 md:grid-cols-2">
                {/* Left column: Top Contributors (Team Members + External) */}
                {topContributors.length > 0 && (
                  <ColorCodedBarChart
                    title={i18nStore.t("topContributorsTitle")}
                    description={i18nStore.tf("dashboardTeamSubtitle", { team: selectedTeam })}
                    data={topContributors}
                    primaryLabel={i18nStore.t("topContributorsTeamMembersLabel")}
                    secondaryLabel={i18nStore.t("topContributorsExternalLabel")}
                    renderAvatars={true}
                  />
                )}

                {/* Right column: Top Repositories */}
                {topTeamRepositories.length > 0 && (
                  <SimpleBarChart
                    title={i18nStore.t("topTeamRepositoriesTitle")}
                    description={i18nStore.t("topTeamRepositoriesDescription")}
                    data={topTeamRepositories}
                    renderAvatars={true}
                    avatarType="repository"
                    getOrganizationName={(repoName) => {
                      const parts = repoName.split("/")
                      return parts.length > 1 ? parts[0] : undefined
                    }}
                  />
                )}
              </div>
            )}

            {/* Details Table - only show when NOT performance */}
            {dashboardStore.selectedInsight?.id !== "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <DataTable
                  title={i18nStore.t("chartDetails")}
                  // biome-ignore lint/suspicious/noExplicitAny: MST frozen type requires any cast
                  columns={dashboardStore.selectedTableColumns as any}
                  data={dashboardStore.selectedTableData}
                  enableSearch={true}
                  searchPlaceholder={i18nStore.t("dataTableSearch")}
                  enableCsvExport={true}
                  csvFilename="insight-data.csv"
                  translations={{
                    filtersButton: i18nStore.t("dataTableFilters"),
                    filterBy: i18nStore.t("dataTableFilterBy"),
                    noResults: i18nStore.t("dataTableNoResults"),
                    rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                    page: i18nStore.t("dataTablePage"),
                    of: i18nStore.t("dataTableOf"),
                    totalRows: i18nStore.t("dataTableTotalRows"),
                  }}
                />
              )}
          </div>
        ),
      },
      {
        id: "commits",
        label: i18nStore.t("tabScore"),
        content: (
          <div className="grid gap-6 md:grid-cols-2">
            <StackedBarChart
              title={i18nStore.t("chartCommitsByClassification")}
              description="Number of commits grouped by classification (commit type) and stacked by complexity size"
              data={barChartData.classificationData}
              categoryKey="classification"
              stackKeys={barChartData.complexityOrder}
            />
            <StackedBarChart
              title={i18nStore.t("chartCommitsBySize")}
              description="Number of commits grouped by complexity size and stacked by classification (commit type)"
              data={barChartData.sizeData}
              categoryKey="size"
              stackKeys={barChartData.activeTypes}
            />
          </div>
        ),
      },
      {
        id: "prs",
        label: i18nStore.t("tabPRs"),
        content: (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <MetricsCard
                title={i18nStore.t("chartPRsMerged")}
                description={i18nStore.tf("chartPRsMergedDesc", {
                  count: prMetrics.currentCount.toString(),
                  prevCount: prMetrics.previousCount.toString(),
                })}
                percentage={Math.abs(prMetrics.percentageChange)}
                trend={prMetrics.trend}
                chartData={prMetrics.prChartData}
                chartType="bar"
              />
              <MetricsCard
                title={i18nStore.t("chartCodeChanges")}
                description={i18nStore.t("chartCodeChangesDesc")}
                percentage={0}
                trend="neutral"
                chartData={prMetrics.codeChangesChartData}
                chartType="line"
              />
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <StackedBarChart
                title={i18nStore.t("chartPRsMergedByWeek")}
                description={i18nStore.t("chartPRsMergedByWeekDesc")}
                data={weeklyPRData.prsByWeekByContributor}
                categoryKey="week"
                stackKeys={weeklyPRData.contributors}
              />
              <DivergingBarChart
                title={i18nStore.t("chartCodeChangesByWeek")}
                description={i18nStore.t("chartCodeChangesByWeekDesc")}
                data={weeklyPRData.codeChangesByWeek}
                positiveLabel={i18nStore.t("linesAdded")}
                negativeLabel={i18nStore.t("linesRemoved")}
              />
            </div>
          </div>
        ),
      },
      {
        id: "issues",
        label: i18nStore.t("tabIssues"),
        content: (
          <div className="grid gap-6 md:grid-cols-2">
            <MetricsCard
              title={i18nStore.t("chartIssuesCreated")}
              description={i18nStore.tf("chartIssuesCreatedDesc", {
                count: issueMetrics.createdCount.toString(),
                prevCount: issueMetrics.previousCreatedCount.toString(),
              })}
              percentage={Math.abs(issueMetrics.createdPercentageChange)}
              trend={issueMetrics.createdTrend}
              chartData={issueMetrics.createdChartData}
              chartType="bar"
            />
            <MetricsCard
              title={i18nStore.t("chartIssuesClosed")}
              description={i18nStore.tf("chartIssuesClosedDesc", {
                count: issueMetrics.closedCount.toString(),
                prevCount: issueMetrics.previousClosedCount.toString(),
              })}
              percentage={Math.abs(issueMetrics.closedPercentageChange)}
              trend={issueMetrics.closedTrend}
              chartData={issueMetrics.closedChartData}
              chartType="bar"
            />
          </div>
        ),
      },
      {
        id: "releases",
        label: i18nStore.t("tabReleases"),
        content: (
          <div className="grid gap-6">
            {/* Releases Trend Card */}
            <MetricsCard
              title={i18nStore.t("releasesTitle")}
              description={i18nStore.t("releasesDescription")}
              percentage={Math.abs(releasesMetrics.percentageChange)}
              trend={releasesMetrics.trend}
              chartData={releasesMetrics.weeklyTrend}
              chartType="line"
              trendLabel={i18nStore.t("chartTrend")}
            />

            {/* Stacked Bar Chart - Releases by Repository */}
            <StackedBarChart
              title={i18nStore.t("releasesChartTitle")}
              description={i18nStore.t("releasesChartDescription")}
              data={releasesMetrics.stackedData}
              categoryKey="week"
              stackKeys={releasesMetrics.repositories}
            />
          </div>
        ),
      },
    ]

    // Log dashboard render
    logger.debug("Rendering TeamDashboard", {
      component: "TeamDashboard",
      team: selectedTeam,
      teamMembers: teamAuthorHandles.size,
    })

    // If no teams available, show message
    if (teams.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          {i18nStore.t("dashboardTeamNoData")}
        </div>
      )
    }

    // Helper to generate stable team avatar fallback (first 2 letters of team name)
    const getTeamAvatarFallback = (teamName: string): string => {
      return teamName.slice(0, 2).toUpperCase()
    }

    return (
      <div className="space-y-6">
        {/* Team selector */}
        <PageHeader
          title=""
          subtitle={i18nStore.tf("dashboardTeamSubtitle", {
            team: selectedTeam,
          })}
          selectOptions={teamOptions}
          selectedOptionId={selectedTeam}
          onSelectChange={handleTeamChange}
          selectPlaceholder={i18nStore.t("selectTeam")}
          selectSearchPlaceholder={i18nStore.t("searchTeams")}
          selectNoResultsText={i18nStore.t("noSearchResults")}
          avatar={{
            fallback: getTeamAvatarFallback(selectedTeam),
            alt: selectedTeam,
          }}
        />
        <TabsWithStore
          tabs={tabs}
          selectedTab={selectedTab}
          defaultSelectedTab="insights"
          onTabChange={onTabChange}
        />
      </div>
    )
  },
)
